/**
 * Which severity a snackbar message should carry.
 *
 * The Domains page decided this with
 * `snackbarMessage.includes('successfully') ? 'success' : 'error'` — severity
 * inferred from one word of copy. So "Domains imported." showed a red error icon
 * over a message reporting success, which is what was reported, and it was not
 * alone: "All selected domains already have monitoring enabled", "All selected
 * domains are paused. No status check needed" and "Please wait a moment before
 * checking CMS again" were all rendered as failures too.
 *
 * Severity is a fact the caller KNOWS. `notifySeverity` exists for the messages
 * whose call sites have not been converted to pass it explicitly yet — it is a
 * stopgap with tests, not the design. Prefer passing severity at the call site.
 *
 * Order matters: failure is checked first, because "Failed to enable monitoring"
 * contains "enable" and must not be read as a success.
 */
const FAILURE = /\b(fail(ed|s|ure)?|error|couldn.t|could not|cannot|can not|unable|denied|rejected|invalid|went wrong|try again)\b/i;
// A limit that BLOCKED the action. Checked before the advisory rule, because
// "You have already reached your 50 domains limit" contains "already" and would
// otherwise be filed as "nothing happened" — when in fact the user's action was
// refused and they need to do something about it.
const BLOCKED = /\b(reached your|limit for this plan|limit exceeded|domain limit|upgrade your plan)\b/i;
// A REFUSAL: the sentence reports that something did not or will not happen.
// This guard exists because the success wordlist below matches state words that
// occur just as often inside negative sentences — "Your account is temporarily
// DISABLED", "temporarily blocked/DELETED", "Deletion won't be allowed as you have
// ADDED keywords". All three classified as success on the first pass. A false
// success is the one direction that must never ship: it tells someone their
// blocked account worked. So refusals are settled before success is considered.
const REFUSED = /\b(won.t|will not|can.t|isn.t|aren.t|not allowed|no longer|must be|temporarily (disabled|blocked|deleted)|blocked|suspended|contact (customer )?(support|admin)|expired)\b/i;
const ADVISORY = /\b(no domains selected|nothing selected|already|no status check needed|please wait|please select|no domains|before checking|coming soon|waiting to)\b/i;
const SUCCESS = /\b(success(fully)?|imported|deleted|updated|enabled|disabled|activated|exported|added|saved|complete[d]?|removed)\b/i;

/**
 * @param {string} message
 * @returns {'success'|'info'|'error'}
 */
export function notifySeverity(message) {
    const m = String(message || '');
    if (!m.trim()) return 'info';
    if (FAILURE.test(m)) return 'error';
    if (BLOCKED.test(m)) return 'error';
    if (REFUSED.test(m)) return 'error';
    // "Advisory" is the class the old rule had no room for: nothing failed, but
    // nothing happened either. Showing a red error for "no domains selected" tells
    // the user something broke when they simply have not chosen anything yet.
    if (ADVISORY.test(m)) return 'info';
    if (SUCCESS.test(m)) return 'success';
    return 'info';
}

export default notifySeverity;
